import * as React from "react";
import { Alert, Button, DropdownItem } from "@jhool-io/fe-components";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";
import WarningIcon from "../../../../components/Icons/Warning";
import InfoIcon from "../../../../icons/Info";
import TimesIcon from "../../../../icons/Times";
import { UserPermisions, UserRole } from "../../../../utils/types/user";
import { IAiSessionNoteReview } from "../../types/notes.types";
import {
    AiReviewFormField,
    filterMeaningfulAiReviewText,
    formatAiReviewLabel,
    getAiReviewFieldFeedbackId,
    getAiReviewFieldsNeedingAttention,
    getAiReviewForFormField,
    isAiSessionNotesEnabled,
} from "../../helpers/aiSessionNotes";
import AiGeneratingRobot from "../AiGeneratingRobot/AiGeneratingRobot";
import {
    BeforeAiSessionNoteReview,
    useAiSessionNoteReview,
    useOptionalAiSessionNoteReview,
} from "../../../../context/AiSessionNoteReviewContext";

const REVIEW_PROGRESS_MESSAGES = [
    "Mantle AI is putting on its reading glasses…",
    "Checking that the note tells a clear clinical story…",
    "Reviewing goals, progress, and next steps…",
    "Looking for details that may need your clinical eye…",
    "Giving the documentation one last thoughtful read…",
];

function withOccurrenceKeys<T>(items: T[], getBaseKey: (item: T) => string) {
    const occurrences = new Map<string, number>();
    return items.map((item) => {
        const baseKey = getBaseKey(item);
        const occurrence = (occurrences.get(baseKey) ?? 0) + 1;
        occurrences.set(baseKey, occurrence);
        return { item, key: `${baseKey}-${occurrence}` };
    });
}

function TextList({
    values,
    className = "text-sm",
}: {
    values: string[];
    className?: string;
}) {
    if (values.length === 0) return null;
    return (
        <ul
            className={["list-disc space-y-1 break-words pl-5", className].join(
                " "
            )}
        >
            {withOccurrenceKeys(values, (value) => value).map(
                ({ item: value, key }) => (
                    <li key={key}>{value}</li>
                )
            )}
        </ul>
    );
}

interface FieldFeedbackState {
    findings: string[];
    suggestions: string[];
}

function useFieldFeedbackState(
    field: AiReviewFormField
): FieldFeedbackState | null {
    const context = useOptionalAiSessionNoteReview();
    if (!context?.review || context.isStale) return null;
    const fieldReview = getAiReviewForFormField(context.review, field);
    if (!fieldReview) return null;
    const findings = filterMeaningfulAiReviewText(fieldReview.findings);
    const suggestions = filterMeaningfulAiReviewText(fieldReview.suggestions);
    const needsAttention =
        fieldReview.status === "missing" ||
        fieldReview.status === "needs_improvement";
    if (!needsAttention) return null;
    return {
        findings,
        suggestions,
    };
}

function AiSessionNoteReviewFieldAnnotationContent({
    feedback,
    field,
}: {
    feedback: FieldFeedbackState;
    field: AiReviewFormField;
}) {
    const [detailsOpen, setDetailsOpen] = React.useState(false);
    const detailsId = React.useId();
    const { findings, suggestions } = feedback;
    const primaryFinding = findings[0];
    const primarySuggestion = suggestions[0];
    let primaryFeedback = "";
    if (primarySuggestion) {
        primaryFeedback = primarySuggestion;
    } else if (primaryFinding) {
        primaryFeedback = primaryFinding;
    } else {
        primaryFeedback = "This field needs clinician review.";
    }
    const additionalDetails = (
        primarySuggestion
            ? [...findings, ...suggestions.slice(1)]
            : findings.slice(primaryFinding ? 1 : 0)
    ).filter((detail) => detail.trim() !== primaryFeedback.trim());
    const fieldLabel = formatAiReviewLabel(field);
    const fieldFeedbackId = getAiReviewFieldFeedbackId(field);

    return (
        <div
            aria-label={`${fieldLabel} Mantle AI feedback`}
            id={fieldFeedbackId}
            role="note"
            tabIndex={-1}
            className={[
                "mt-1 min-w-0 bg-transparent text-[11px] font-normal leading-4",
                "scroll-mt-24 rounded-sm [overflow-wrap:anywhere]",
                "focus:outline-none focus:ring-2 focus:ring-primary",
            ].join(" ")}
        >
            <div className="flex min-w-0 items-start gap-1">
                <span
                    className={[
                        "mt-px inline-flex shrink-0 items-center gap-1 rounded-sm px-1",
                        "bg-[var(--color-warning-bg)] font-medium",
                        "[color:var(--color-warning-text)] [&_svg]:size-3",
                    ].join(" ")}
                >
                    <span aria-hidden="true">
                        <WarningIcon />
                    </span>
                    <span>Needs attention</span>
                </span>
                <span aria-hidden="true" className="shrink-0 text-textSec">
                    —
                </span>
                <span
                    className={[
                        "line-clamp-2 min-w-0 max-w-[80ch] flex-1 break-words",
                        "text-textSec sm:line-clamp-1",
                    ].join(" ")}
                >
                    {primaryFeedback}
                </span>
                {additionalDetails.length > 0 ? (
                    <button
                        aria-controls={detailsId}
                        aria-expanded={detailsOpen}
                        aria-label={`Details for ${fieldLabel} review feedback`}
                        type="button"
                        onClick={() => setDetailsOpen((open) => !open)}
                        className={[
                            "relative -my-1 inline-flex h-6 shrink-0 appearance-none",
                            "items-center border-0 bg-transparent px-1 text-[11px]",
                            "font-medium text-textLink underline underline-offset-2",
                            "before:absolute before:-inset-x-1 before:-inset-y-2.5",
                            "before:content-[''] focus:outline-none focus-visible:rounded",
                            "focus-visible:ring-2 focus-visible:ring-primary",
                        ].join(" ")}
                    >
                        Details
                    </button>
                ) : null}
            </div>
            {additionalDetails.length > 0 ? (
                <div
                    aria-label={`${fieldLabel} AI feedback details`}
                    hidden={!detailsOpen}
                    id={detailsId}
                    role="region"
                    className={[
                        "ml-4 mt-1 max-w-[80ch] border-l border-stroke pl-2",
                        "space-y-1 break-words text-[11px] leading-4 text-textSec",
                    ].join(" ")}
                >
                    <TextList
                        values={additionalDetails}
                        className="text-[11px] font-normal"
                    />
                </div>
            ) : null}
        </div>
    );
}

export function AiSessionNoteReviewFieldAnnotation({
    field,
}: {
    field: AiReviewFormField;
}) {
    const feedback = useFieldFeedbackState(field);
    if (!isAiSessionNotesEnabled() || !feedback) return null;

    return (
        <AiSessionNoteReviewFieldAnnotationContent
            field={field}
            feedback={feedback}
        />
    );
}

function AiSessionNoteReviewProgressModal({ open }: { open: boolean }) {
    const [messageIndex, setMessageIndex] = React.useState(0);
    const dialogRef = React.useRef<HTMLDialogElement>(null);

    React.useEffect(() => {
        if (!open) return undefined;
        setMessageIndex(0);
        const interval = setInterval(() => {
            setMessageIndex(
                (current) => (current + 1) % REVIEW_PROGRESS_MESSAGES.length
            );
        }, 3500);
        return () => clearInterval(interval);
    }, [open]);

    React.useEffect(() => {
        if (!open) return undefined;
        const previouslyFocused = document.activeElement;
        const dialog = dialogRef.current;
        if (!dialog) return undefined;
        if (typeof dialog.showModal === "function") {
            dialog.showModal();
        } else {
            dialog.setAttribute("open", "");
        }
        dialog.focus();
        return () => {
            if (typeof dialog.close === "function" && dialog.open) {
                dialog.close();
            }
            if (previouslyFocused instanceof HTMLElement) {
                previouslyFocused.focus();
            }
        };
    }, [open]);

    if (!open) return null;

    return (
        <dialog
            aria-modal="true"
            aria-labelledby="ai-review-progress-title"
            ref={dialogRef}
            tabIndex={-1}
            onCancel={(event) => event.preventDefault()}
            className={[
                "fixed inset-0 z-50 m-0 flex size-full max-h-none max-w-none",
                "items-center justify-center border-0 bg-backdrop p-4",
            ].join(" ")}
        >
            <div
                className={[
                    "w-full max-w-[420px] rounded-md bg-[var(--card-bg)] p-6",
                    "text-text shadow-dropdown focus:outline-none",
                ].join(" ")}
            >
                <h2
                    id="ai-review-progress-title"
                    className="mb-5 text-center text-title font-semibold"
                >
                    Reviewing note with Mantle AI
                </h2>
                <div
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    className="flex flex-col items-center gap-4 text-center"
                >
                    <span
                        className={[
                            "flex size-14 items-center justify-center",
                            "rounded-full bg-[var(--color-info-bg)]",
                            "[color:var(--color-info-text)]",
                        ].join(" ")}
                    >
                        <AiGeneratingRobot className="size-8" />
                    </span>
                    <p className="font-medium">
                        {REVIEW_PROGRESS_MESSAGES[messageIndex]}
                    </p>
                    <p className="text-sm text-textSec">
                        Keep this window open while the review finishes.
                    </p>
                </div>
            </div>
        </dialog>
    );
}

interface ReviewFieldSummary {
    countSummary: string;
    issueFields: AiReviewFormField[];
}

function getReviewFieldSummary(
    review: IAiSessionNoteReview
): ReviewFieldSummary {
    const attentionFields = getAiReviewFieldsNeedingAttention(review);
    const attentionCount = attentionFields.length;
    let countSummary = "No field-level issues found";
    if (attentionCount === 1) {
        countSummary = "1 field needs attention";
    } else if (attentionCount > 1) {
        countSummary = `${attentionCount} fields need attention`;
    }
    return {
        countSummary,
        issueFields: attentionFields,
    };
}

export function AiSessionNoteReviewPanel({
    beforeReview,
}: {
    beforeReview?: BeforeAiSessionNoteReview;
}) {
    const {
        review,
        phase,
        errorMessage,
        isStale,
        focusRequestKey,
        isPending,
        runReview,
        dismissReview,
    } = useAiSessionNoteReview();
    const sectionRef = React.useRef<HTMLElement>(null);
    const reviewFieldSummary = review ? getReviewFieldSummary(review) : null;
    let actionLabel = errorMessage ? "Retry review" : "Review again";
    if (phase === "saving") actionLabel = "Saving note…";
    if (phase === "reviewing") actionLabel = "Reviewing note…";

    React.useEffect(() => {
        if (focusRequestKey === 0 || !sectionRef.current) return;
        sectionRef.current.scrollIntoView?.({ block: "start" });
        sectionRef.current.focus({ preventScroll: true });
    }, [errorMessage, focusRequestKey, review]);

    if (!isAiSessionNotesEnabled()) return null;

    const handleGoToFirstIssue = () => {
        const issueFields = reviewFieldSummary?.issueFields ?? [];
        const fieldFeedback = issueFields
            .map((field) =>
                document.getElementById(getAiReviewFieldFeedbackId(field))
            )
            .find((element): element is HTMLElement => element !== null);
        fieldFeedback?.scrollIntoView({ block: "center" });
        fieldFeedback?.focus({ preventScroll: true });
    };

    if (!review && !errorMessage) {
        return <AiSessionNoteReviewProgressModal open={isPending} />;
    }

    return (
        <>
            <AiSessionNoteReviewProgressModal open={isPending} />
            <section
                id="ai-session-note-review"
                ref={sectionRef}
                tabIndex={-1}
                aria-label="Mantle AI review"
                className={[
                    "sticky top-20 z-[4] mb-4 max-h-[calc(100dvh-6rem)]",
                    "overflow-hidden rounded-md border border-solid bg-[var(--card-bg)]",
                    "border-[var(--color-info-text)]",
                    "[background-image:linear-gradient(var(--color-info-bg),var(--color-info-bg))]",
                    "px-2 py-1 text-[var(--color-info-text)]",
                    "sm:px-3",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                ].join(" ")}
            >
                {errorMessage ? (
                    <Alert
                        type="error"
                        title="Review unavailable"
                        description={errorMessage}
                    />
                ) : null}
                {review ? (
                    <div className="flex flex-wrap items-center gap-x-3 pr-8">
                        <div className="flex min-w-0 items-center gap-2">
                            <span
                                aria-hidden="true"
                                className="flex size-4 shrink-0 items-center justify-center"
                            >
                                <InfoIcon className="size-4" />
                            </span>
                            <p className="min-w-0 flex-1 text-[13px] leading-5">
                                <span className="font-semibold">
                                    Mantle AI Review
                                </span>
                                {" - "}
                                <span>{reviewFieldSummary?.countSummary}</span>
                                {isStale ? (
                                    <span
                                        title={[
                                            "Review may be outdated.",
                                            "Review again after current edits.",
                                        ].join(" ")}
                                        className={[
                                            "text-[11px] font-medium leading-4",
                                            "ml-2 inline-block rounded-sm px-1",
                                            "bg-[var(--color-warning-bg)]",
                                            "[color:var(--color-warning-text)]",
                                        ].join(" ")}
                                    >
                                        Outdated
                                        <span className="sr-only">
                                            . Review again after current edits.
                                        </span>
                                    </span>
                                ) : null}
                            </p>
                            <button
                                type="button"
                                aria-label="Dismiss Mantle AI review"
                                className={[
                                    "absolute right-0 top-0 flex size-[44px] appearance-none",
                                    "items-center justify-center border-0 bg-transparent p-2",
                                    "text-[var(--color-info-text)] focus:outline-none",
                                    "focus-visible:ring-2",
                                    "focus-visible:ring-primary",
                                ].join(" ")}
                                disabled={isPending}
                                onClick={dismissReview}
                            >
                                <span className="flex size-7 items-center justify-center rounded">
                                    <TimesIcon className="size-3" />
                                </span>
                            </button>
                        </div>
                        <div
                            className={[
                                "ml-5 flex flex-wrap items-center gap-x-1",
                                "sm:ml-0",
                            ].join(" ")}
                        >
                            {!isStale ? (
                                <>
                                    <button
                                        type="button"
                                        className={[
                                            "flex min-h-[44px] appearance-none items-center",
                                            "border-0 bg-transparent p-1",
                                            "text-[var(--color-info-text)]",
                                            "focus:outline-none focus-visible:ring-2",
                                            "focus-visible:ring-primary",
                                        ].join(" ")}
                                        disabled={
                                            isPending ||
                                            reviewFieldSummary?.issueFields
                                                .length === 0
                                        }
                                        onClick={handleGoToFirstIssue}
                                    >
                                        <span
                                            className={[
                                                "inline-flex h-7 items-center px-1",
                                                "text-[11px] font-medium underline",
                                                "underline-offset-2",
                                            ].join(" ")}
                                        >
                                            Go to first issue
                                        </span>
                                    </button>
                                    <span
                                        aria-hidden="true"
                                        className="text-[11px]"
                                    >
                                        /
                                    </span>
                                </>
                            ) : null}
                            <button
                                type="button"
                                className={[
                                    "flex min-h-[44px] appearance-none items-center",
                                    "border-0 bg-transparent p-1 focus:outline-none",
                                    "focus-visible:ring-2 focus-visible:ring-primary",
                                ].join(" ")}
                                disabled={isPending}
                                onClick={() => runReview(beforeReview)}
                            >
                                <span
                                    className={[
                                        "inline-flex h-7 items-center px-1",
                                        "text-[11px] font-medium underline underline-offset-2",
                                        "text-[var(--color-info-text)]",
                                    ].join(" ")}
                                >
                                    {actionLabel}
                                </span>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-wrap items-center gap-1">
                        <Button
                            type="button"
                            size="small"
                            className="!min-h-[44px] !px-3 !py-1 text-sm"
                            disabled={isPending}
                            onClick={() => runReview(beforeReview)}
                        >
                            {actionLabel}
                        </Button>
                        <Button
                            type="button"
                            variant="normal"
                            size="auto"
                            className={[
                                "!min-h-[44px] !px-2 text-sm text-textSec",
                                "underline underline-offset-2",
                            ].join(" ")}
                            disabled={isPending}
                            onClick={dismissReview}
                        >
                            Dismiss
                        </Button>
                    </div>
                )}
            </section>
        </>
    );
}

export function AiSessionNoteReviewTrigger({
    beforeReview,
}: {
    beforeReview?: BeforeAiSessionNoteReview;
}) {
    const { phase, isPending, runReview } = useAiSessionNoteReview();
    const canUseAi = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_NOTES_AI_REVIEW,
    ]);
    const isClinician = useDoesUserHaveRole([
        UserRole.PROVIDER,
        UserRole.SUPER_ADMIN,
    ]);

    if (!isAiSessionNotesEnabled() || !canUseAi || !isClinician) return null;

    let triggerLabel = "Review note with Mantle AI";
    if (phase === "saving") triggerLabel = "Saving note…";
    if (phase === "reviewing") triggerLabel = "Reviewing note…";

    return (
        <Button
            type="button"
            disabled={isPending}
            aria-controls="ai-session-note-review"
            onClick={() => runReview(beforeReview)}
        >
            {triggerLabel}
        </Button>
    );
}

export function AiSessionNoteReviewMenuItem({
    onReviewStart,
}: {
    onReviewStart(): void;
}) {
    const { isPending, runReview } = useAiSessionNoteReview();

    if (!isAiSessionNotesEnabled()) return null;

    const handleReview = () => {
        onReviewStart();
        runReview();
    };

    return (
        <DropdownItem disabled={isPending} onClick={handleReview}>
            {isPending ? "Reviewing note…" : "Review note with Mantle AI"}
        </DropdownItem>
    );
}
